import { Component, computed, effect, inject, Input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { SearchService } from '../../../../services/bidder-community/search/search.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SelectModule } from 'primeng/select';
import { faImage, faList } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormsModule } from '@angular/forms';
import { ItemVerticalTemplateComponent } from '../../items/item-vertical-template/item-vertical-template.component';
import { ItemHorizontalTemplateComponent } from '../../items/item-horizontal-template/item-horizontal-template.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PaginatorModule } from 'primeng/paginator';
import { MySearchesService } from '../../../../services/bidder-community/my-searches/my-searches.service';
import { AuthService, LOGIN_TYPE_TOPBAR } from '../../../../services/authentication/auth.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { distinctUntilChanged, skip, Subscription, tap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { CustomBreadcrumbComponent } from '../../custom-breadcrumb/custom-breadcrumb.component';
import { CustomBreadcrumbService } from '../../../../services/custom-breadcrumb/custom-breadcrumb.service';
import { AbsoluteMediaUriService } from '../../../../services/absolute-media-uri.service';
import { ICustomMenuItem } from '../../../../models/ICustomMenuItem';
import { MenuItem } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';


@Component({
  selector: 'app-items-only',
  imports: [FontAwesomeModule,
    SelectModule,
    ButtonModule,
    FormsModule,
    SelectButtonModule,
    ItemVerticalTemplateComponent,
    PaginatorModule,
    ItemHorizontalTemplateComponent,
    ProgressSpinnerModule,
    CustomBreadcrumbComponent],
  templateUrl: './items-only.component.html',
  styleUrl: './items-only.component.scss'
})
export class ItemsOnlyComponent implements OnInit {

  @Input() query: string | null = null;

  private readonly searchService = inject(SearchService);
  private readonly searchAccountService = inject(MySearchesService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  authSubscription!: Subscription;

  isAuthenticated = this.oidcSecurityService.authenticated;
  itemsResultSignal = this.searchService.itemsResultSignal;

  private readonly platformId = inject(PLATFORM_ID);

  items = computed(() => this.itemsResultSignal()?.items ?? []);
  itemsTotal = computed(() => this.itemsResultSignal()?.total ?? 0);

  faImage = faImage;
  faList = faList;

  protected icons = {
    faImage: faImage,
    faList: faList
  }
  viewMode: 'image' | 'list' = 'image';
  ItemListOptions: any[] = [{ label: 'Image View', value: 'image' }, { label: 'List View', value: 'list' }];

  page: number = 0;
  rowsPerPageOptions = [15, 30, 45, 60];
  first = signal(0);
  per = signal(30);

  selectedSortSignal = signal('NAME_ASC');

  isMobile!: boolean;

  // breadcrumb
  private readonly customBreadcrumbService = inject(CustomBreadcrumbService);
  private readonly absoluteMediaUriService = inject(AbsoluteMediaUriService);

  itemsBreadcrumb: ICustomMenuItem[] | undefined;
  home!: MenuItem;


  sortFilters = [
    { label: 'Item Name - Ascending', value: 'NAME_ASC' },
    { label: 'Item Name - Descending', value: 'NAME_DESC' },
    { label: 'Value - Ascending', value: 'VALUE_ASC' },
    { label: 'Value - Descending', value: 'VALUE_DESC' },
    { label: 'Current Bid - Ascending', value: 'CURRENT_BID_ASC' },
    { label: 'Current Bid - Descending', value: 'CURRENT_BID_DESC' },
    // { label: 'Auction - Ascending', value: 'AUCTION_ASC' },
    // { label: 'Auction - Descending', value: 'AUCTION_DESC' },
    { label: 'Number of Bids - Ascending', value: 'NUM_BIDS_ASC' },
    { label: 'Number of Bids - Descending', value: 'NUM_BIDS_DESC' },
    { label: 'Time Left - Ascending', value: 'CLOSE_ASC' },
    { label: 'Time Left - Descending', value: 'CLOSE_DESC' },
  ];



  constructor(private route: ActivatedRoute) {
    effect(() => {
      const items = this.items();
    })

  }

  ngOnInit(): void {

    if (isPlatformBrowser(this.platformId)) {
      this.isMobile = window.innerWidth < 1024;
      if (this.isMobile) {
        this.viewMode = 'image';
      } else {
        this.viewMode = this.viewMode ?? 'list';
      }

      window.addEventListener('resize', () => {
        this.viewMode = window.innerWidth < 1200
          ? 'image'
          : (this.viewMode ?? 'image');
      });

    }

    this.route.queryParams.subscribe(params => {
      this.query = params['q'] ?? '';
      if (this.query) {
        this.getItems(this.query);
      }
    });

    this.authSubscription = this.authService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        skip(1),
        tap(isAuth => {
          if (isAuth) {
            this.getItems(this.query ?? '');

          } else {
            this.getItems(this.query ?? '');
          }
        })
      ).subscribe();
  }

  getItems(q: string) {
    this.searchService.getItemsResult(q ?? '', this.page, this.per(), this.selectedSortSignal()).subscribe(result => {

      const total = result.total ?? 0;

      this.itemsBreadcrumb = [
        {
          label: 'BiddingForGood',
          url: this.absoluteMediaUriService.getAbsoluteMediaUri('/auction/biddingforgood.action')
        },
        { label: `Search Results (${total})` },
      ];

      this.customBreadcrumbService.setItems(this.itemsBreadcrumb);
    });
  }

  onPageChange(event: any) {
    this.first.set(event.first);
    this.per.set(event.rows);

    this.page = event.page + 1;

    this.getItems(this.query!);
  }

  onSortChange(event: any): void {
    this.selectedSortSignal.set(event.value);
    this.first.set(0);
    this.page = 1;

    this.getItems(this.query ?? '');

  }

  openLoginModal(fun?: () => void) {
    this.authService.openLoginModal(LOGIN_TYPE_TOPBAR, fun ?? (() => { })
    );
  }


  toggleSaveSearch(q: string) {

    if (this.isAuthenticated().isAuthenticated) {
      this.saveNewSearch(q);

    } else {
      this.openLoginModal(() => this.saveNewSearch(q));
    }


  }

  saveNewSearch(q: string) {
    this.searchAccountService.postMySearch(q).subscribe({
      next: () => {
        this.router.navigate(['/my-account/my-searches']);
      }
    });
  }

  onFavoriteChanged(event: { isFavorite: boolean, itemId: number }): void {
    this.searchService.itemsResultSignal.update(result => {
      if (!result) return result;

      return {
        ...result,
        items: result.items.map(item =>
          item.id === event.itemId
            ? { ...item, isFavorite: !event.isFavorite }
            : item
        )
      };
    });

  }



}
